// TPR trend chart (Temperature / Pulse / Respiration) plotted over a patient's
// recorded VitalSign history (D10, Epic 9 — Nursing Charts).
//
// chart.js is v3.9.1 here (see package.json), which — unlike v2 — requires
// every controller/element/scale/plugin it uses to be explicitly registered
// via ChartJS.register(...); skipping this makes the chart silently fail to
// render. The two other repo call sites for react-chartjs-2
// (src/components/PrescriptionCard/PrescriptionCard.js,
// src/pages/patient-trend-analysis/index.js) import `Line` with no
// registration at all, so they are not a safe pattern to copy — this file
// registers explicitly instead.
import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Legend,
  Tooltip,
} from "chart.js";
import { Box, Typography } from "@mui/material";
import { istDayKey, formatIstTime } from "../../../utils/istDateTime";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, Legend, Tooltip);

// recorded_at is a UTC instant; slicing its raw digits labelled every point
// 5h30m early. istDayKey drops the year to keep the axis label short.
const fmtLabel = (dt) => {
  const day = istDayKey(dt);
  return day ? `${day.slice(5)} ${formatIstTime(dt)}` : "";
};

// Temperature (~35-41°C) shares no sensible scale with pulse (bpm) or
// respiration (breaths/min) — on one axis the temperature line would flatten
// to a near-invisible band. Temperature gets its own left axis; pulse and
// respiration (both "count per minute") share a right axis.
export default function TprChart({ vitals = [] }) {
  const sorted = useMemo(
    () => [...vitals].sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at)),
    [vitals]
  );

  if (sorted.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
        No vitals recorded yet to chart.
      </Typography>
    );
  }

  // recorded_at is required by the model, but temperature / heart_rate /
  // respiratory_rate are each independently nullable (a nurse may record only
  // some fields per reading). `?? null` keeps missing readings as chart.js
  // gaps in the line (its documented null-skip behaviour) rather than
  // coercing to 0, which would draw a false dip.
  const data = {
    labels: sorted.map((v) => fmtLabel(v.recorded_at)),
    datasets: [
      {
        label: "Temperature (°C)",
        data: sorted.map((v) => v.temperature ?? null),
        borderColor: "#2a78d6",
        backgroundColor: "#2a78d6",
        yAxisID: "temp",
        tension: 0.2,
      },
      {
        label: "Pulse (bpm)",
        data: sorted.map((v) => v.heart_rate ?? null),
        borderColor: "#1baf7a",
        backgroundColor: "#1baf7a",
        yAxisID: "count",
        tension: 0.2,
      },
      {
        label: "Respiration (breaths/min)",
        data: sorted.map((v) => v.respiratory_rate ?? null),
        borderColor: "#eda100",
        backgroundColor: "#eda100",
        yAxisID: "count",
        tension: 0.2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    scales: {
      temp: { type: "linear", position: "left", title: { display: true, text: "°C" } },
      count: {
        type: "linear",
        position: "right",
        min: 0,
        title: { display: true, text: "bpm / breaths per min" },
        grid: { drawOnChartArea: false },
      },
    },
    plugins: {
      legend: { position: "bottom" },
    },
  };

  return (
    <Box sx={{ height: 260 }}>
      <Line data={data} options={options} />
    </Box>
  );
}
